import { Case } from '../models/case';
import { Partner } from '../models/partner';

/**
 * Presentation helpers for case rows, shared by the Cases page and the Home dashboard.
 *
 * The tenant ones exist because the hub reads cases across every tenant: a case carries a doctor
 * and a tech partner, and either (or both) may be the tenant that owns it — the backend's own
 * tenant predicate is `doctor.id = tenant OR tech.id = tenant`. Nothing on the case says which,
 * so we read it back off the partners' isTenant flag.
 */

export function caseTenantPartners(caseItem: Case): Partner[] {
  return [caseItem.doctor, caseItem.tech].filter(
    (partner): partner is Partner => !!partner?.isTenant
  );
}

/**
 * A tenant id to address this case with (for X-T-ID on writes). Falls back to the doctor, then the
 * tech, so a case whose partners have no isTenant flag set still resolves to something usable.
 */
export function caseTenantId(caseItem: Case): number | string | undefined {
  return caseTenantPartners(caseItem)[0]?.id ?? caseItem.doctor?.id ?? caseItem.tech?.id;
}

export function caseStatusClass(status?: string | null): string {
  if (!status) return 'status-unknown';

  switch (status.toUpperCase()) {
    case 'DONE':
      return 'status-completed';
    case 'IN_PROGRESS':
      return 'status-active';
    case 'NEW':
      return 'status-pending';
    case 'REQUESTED':
      return 'status-requested';
    case 'ON_HOLD':
      return 'status-on-hold';
    case 'DECLINED':
      return 'status-cancelled';
    default:
      return 'status-unknown';
  }
}

/** Humanizes a raw enum name, so a status the hub does not know about still reads sanely. */
export function caseStatusLabel(status?: string | null): string {
  if (!status) return 'Unknown';
  return status.replace(/_/g, ' ')
               .toLowerCase()
               .replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Local calendar date as `yyyy-MM-dd`, which is what createdAtFrom/createdAtTo take. The backend
 * widens these to Europe/Belgrade day boundaries.
 */
export function toDateParam(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** `yyyy-MM-dd` for the day `daysBack` days before today (0 = today). */
export function daysAgoParam(daysBack: number): string {
  const date = new Date();
  date.setDate(date.getDate() - daysBack);
  return toDateParam(date);
}
